import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';

import { Provider } from '@app/shared/interfaces/provider.interface';
import { catchError, forkJoin, map, of, Subject, take, takeUntil, timer } from 'rxjs';
import { FormBuilder, FormControl, FormGroup, FormGroupDirective, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { Store } from '@ngrx/store';
import { AppState } from '@app/core/store/app.state';
import { FetchProviders } from '../../store/admin.actions';
import { selectProviders } from '../../store/admin.selectors';
import { MatOption, MatSelect } from "@angular/material/select";

import { MatButton } from "@angular/material/button";
import { AdminService } from "@app/features/adminDashboard/services/admin.service";
import { WebResponse } from "@app/shared/interfaces/webResponse.interface";
import { ToastService } from "@app/core/services/toast.service";
import { LoadSpinnerWrapperComponent } from "@app/shared/components/load-spinner-wrapper/load-spinner-wrapper.component";
import { NgxMatSelectSearchModule } from "ngx-mat-select-search";

@Component({
  selector: 'app-provider-mgmt',
  imports: [
    MatDialogModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatOption,
    MatButton,
    LoadSpinnerWrapperComponent,
    NgxMatSelectSearchModule,
    MatSelect
],
  templateUrl: './provider-mgmt.component.html',
  styleUrl: './provider-mgmt.component.scss'
})
export class ProviderMgmtComponent implements OnInit, OnDestroy {
  onDestroy = new Subject<void>();

  loading = false;
  refreshing = false;
  refreshSecondsRemaining = 0;
  noOptionsText = 'No providers found';

  selectedProvider: string | null = null;
  selectedGroup: string | null = null;
  providersList: Provider[] = [];
  filteredProviderList: Provider[] = [];

  @ViewChild(FormGroupDirective) formDirective?: FormGroupDirective;

  selectedProviderCtrl!: FormControl;
  groupNameCtrl!: FormControl;
  searchBarFormControl: FormControl = new FormControl();

  cpInfoForm: FormGroup = this.fb.group({
    selectedProvider: [null, [Validators.required]],
    groupName: [null],
  });

  constructor(
    private store: Store<AppState>,
    private fb: FormBuilder,
    private adminService: AdminService,
    private toastService: ToastService,
  ) {}

  ngOnInit(): void {
    this.setupFormGroups();
    this.subscribeToProviders();
    this.store.dispatch(FetchProviders());
  }

  ngOnDestroy(): void {
    this.onDestroy.next();
    this.onDestroy.complete();
  }

  private setupFormGroups() {
    this.selectedProviderCtrl = this.cpInfoForm.controls['selectedProvider'] as FormControl;
    this.groupNameCtrl = this.cpInfoForm.controls['groupName'] as FormControl;
  }

  private parseGroupName(groupName: unknown): string | null {
    if (groupName === null || groupName === undefined || groupName === '') {
      return null;
    }

    const parsedId = String(groupName).trim();
    return parsedId.length > 0 ? parsedId : null;
  }

  private subscribeToProviders(): void {
      this.store.select(selectProviders)
        .pipe(takeUntil(this.onDestroy))
        .subscribe((providers: Provider[]) => {
          this.selectedProviderCtrl.reset();
          this.providersList = providers;
          this.filteredProviderList = providers;
      });

    this.selectedProviderCtrl.valueChanges
      .pipe(takeUntil(this.onDestroy))
      .subscribe(value => {
        let provider = this.providersList.find(provider => provider.id === value);
        if(provider) {
          let groupName = this.parseGroupName(provider.groupName);
          this.onProvidersSelectionChange(provider.id, groupName);
        }
    })

    this.searchBarFormControl.valueChanges
      .subscribe(() => {
        this.filterProviders();
      });
  }

  get providerGroups(): string[] {
    return [...new Set(this.providersList
      .map(provider => this.parseGroupName(provider.groupName))
      .filter((groupName): groupName is string => groupName !== null))]
      .sort((firstGroup, secondGroup) => firstGroup.localeCompare(secondGroup));
  }

  filterProviders() {
    const search = (this.searchBarFormControl.value ?? '').toString().trim().toLowerCase();
    if (!search) {
      this.filteredProviderList = [...this.providersList];
    } else {
      this.filteredProviderList =
        this.providersList.filter(option => (`${option.firstName} ${option.lastName}`).toLowerCase().includes(search) ||
          (`${option.lastName}, ${option.firstName}`).toLowerCase().includes(search));
    }
  }

  private resetForm(): void {
    this.filteredProviderList = [];
    this.selectedProvider = null;
    this.selectedGroup = null;
    this.formDirective?.resetForm();
    this.store.dispatch(FetchProviders());
  }

  onProvidersSelectionChange(providerId: string, groupName: string | null) {
    this.selectedProvider = providerId;
    this.groupNameCtrl.setValue(groupName);
  }

  onRemoveProviderFromGroup(): void {
    this.groupNameCtrl.setValue(null);
  }

  onRefresh() {
    this.refreshing = true;
    this.refreshSecondsRemaining = 10;
    timer(0, 1000).pipe(take(11), takeUntil(this.onDestroy)).subscribe(elapsed => {
      this.refreshSecondsRemaining = 10 - elapsed;
    });
    forkJoin([
      this.adminService.refreshProviders().pipe(
        map(() => true),
        catchError(() => of(false))
      ),
      timer(10000)
    ]).pipe(takeUntil(this.onDestroy)).subscribe(([succeeded]) => {
      this.refreshing = false;
      if (succeeded) {
        this.store.dispatch(FetchProviders());
        this.toastService.showSuccessToast('Provider list refreshed successfully.');
      } else {
        this.toastService.showErrorToast('Failed to refresh provider list. Please try again.');
      }
    });
  }

  onSave(){
    this.loading = true;
    this.adminService.updateProviderGroup(this.selectedProviderCtrl.value, this.groupNameCtrl.value).subscribe((response: WebResponse<boolean>) => {
      this.loading = false;
      if (response.succeeded) {
        this.toastService.showSuccessToast('Provider group updated successfully.');
        this.resetForm();
      } else {
        this.toastService.showErrorToast('Something went wrong, please try again.');
      }
    });
  }
}
